import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import useData from "../../hooks/useData";
import useLocalization from "../../hooks/useLocalization";
import usePage from "../../hooks/usePage";

import Page from "./Page";
import Card from "../layout/Card";
import FileUpload from "../elements/FileUpload";
import Switch from "../elements/Switch";
import Date from "../elements/Date";
import Select from "../elements/Select";
import Pagination from "../elements/Pagination";

const ProjectImportExport = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const { getFromServer, addToWorkQueue, toDate } = useData();
  const { getTitle } = useLocalization();
  const { showMessage } = usePage();

  const [data, setData] = useState(null);
  const [currentImport, setCurrentImport] = useState(null);
  const [selectedExport, setSelectedExport] = useState(null);

  const [syndicatedTerritories, setSyndicatedTerritories] = useState(false);
  const [customTerritories, setCustomTerritories] = useState(false);
  const [directOrders, setDirectOrders] = useState(false);
  const [frequency, setFrequency] = useState(false);
  const [dateRange, setDateRange] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate_, setToDate_] = useState("");
  const [scheduleType, setScheduleType] = useState("");

  const clientId = location.state?.clientId;
  const projectId = location.state?.projectId;
  const name = location.state?.name;

  const currentUserId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);

  useEffect(() => {
    if (projectId) {
      getData();
    }
  }, [projectId]);

  useEffect(() => {
    // Reset options when export changes
    setSyndicatedTerritories(false);
    setCustomTerritories(false);
    setDirectOrders(false);
    setFrequency(false);
    setDateRange(false);
    setFromDate("");
    setToDate_("");
    setScheduleType("");

    if (selectedExport === "exportLocationsFromProject") {
      // Don't auto-send; wait for user to configure options
    }
  }, [selectedExport]);

  const getData = async () => {
    const results = await getFromServer({
      entityName: "Project",
      id: projectId,
    });
    if (results) {
      setData(results);
    } else {
      setData(null);
    }
  };

  const handleToolbarClick = (action) => {
    switch (action) {
      case "back":
        navigate("/project", {
          state: { projectId: projectId, clientId: clientId },
        });
        break;
      default:
        break;
    }
  };

  const handleExportSelection = (selection) => {
    setSelectedExport(selection);
  };

  const handleImportSelection = (selection) => {
    setCurrentImport(selection);
  };

  const handleUploaded = () => {
    showMessage("You will receive an email on completion.");
  };

  const handleDateRange = (newValue) => {
    if (!newValue) {
      setFromDate("");
      setToDate_("");
    }
    setDateRange(newValue);
  };

  const sendExportRequest = async () => {
    if (selectedExport === "exportLocationsFromProject") {
      if (dateRange && (!fromDate || !toDate_)) {
        showMessage("Please provide From and To dates");
        return;
      }

      const options = {
        syndicatedTerritories,
        customTerritories,
        directOrders,
        frequency,
        dateRange,
      };

      if (dateRange) {
        options.fromDate = fromDate;
        options.toDate = toDate_;
      }

      await addToWorkQueue("exportLocationsFromProject", {
        projectId,
        email,
        options,
      });

      showMessage("Your file will be emailed to you shortly.");
      return;
    }

    if (selectedExport === "exportProjectSchedule") {
      if (!scheduleType) {
        showMessage("Please select schedule layout");
        return;
      }

      if (scheduleType === "dateColumns" && (!fromDate || !toDate_)) {
        showMessage("Please provide From and To dates");
        return;
      }

      const details = {
        projectId,
        email,
        mode: scheduleType,
      };

      if (scheduleType === "dateColumns") {
        details.fromDate = fromDate;
        details.toDate = toDate_;
      }

      await addToWorkQueue("exportProjectSchedule", details);
      showMessage("Your file will be emailed to you shortly.");
    }
  };

  return (
    <Page
      pageName="Project Imports & Exports"
      entityName={name}
      includeToolbar
      items={[
        {
          name: "back",
          iconName: "ArrowCircleLeft",
        },
      ]}
      onToolItemClick={handleToolbarClick}
    >
      <Box
        sx={{ display: "flex" }}
        gap={1}
      >
        {/* Exports Card */}
        <Card
          title={getTitle("exports")}
          sx={{ width: "300px" }}
        >
          <Stack spacing={1}>
            <Box>
              <Button
                size="small"
                fullWidth
                variant={
                  selectedExport === "exportLocationsFromProject"
                    ? "contained"
                    : "outlined"
                }
                onClick={() =>
                  handleExportSelection("exportLocationsFromProject")
                }
              >
                Export Project Locations
              </Button>
            </Box>
            <Box>
              <Button
                size="small"
                fullWidth
                variant={
                  selectedExport === "exportProjectSchedule"
                    ? "contained"
                    : "outlined"
                }
                onClick={() => handleExportSelection("exportProjectSchedule")}
              >
                Export Project Schedule
              </Button>
            </Box>
          </Stack>
        </Card>

        {/* Imports Card */}
        <Card
          title={getTitle("imports")}
          sx={{ width: "300px" }}
        >
          <Stack>
            <Box>
              <Button
                size="small"
                fullWidth
                onClick={() => handleImportSelection("importProjectLocations")}
              >
                Import Project Locations
              </Button>
            </Box>
          </Stack>
        </Card>

        {/* Export Options Card */}
        {selectedExport === "exportLocationsFromProject" && (
          <Card
            title="Export Options"
            sx={{ width: "300px" }}
          >
            <Stack
              sx={{ padding: "10px" }}
              spacing={1}
            >
              <Switch
                label="Syndicated Territories"
                value={syndicatedTerritories}
                onChange={(newValue) => setSyndicatedTerritories(newValue)}
              />

              <Switch
                label="Custom Territory Column"
                value={customTerritories}
                onChange={(newValue) => setCustomTerritories(newValue)}
              />

              <Switch
                label="Include Direct Orders"
                value={directOrders}
                onChange={(newValue) => setDirectOrders(newValue)}
              />

              {!dateRange && (
                <Switch
                  label="Frequency"
                  value={frequency}
                  onChange={(newValue) => setFrequency(newValue)}
                />
              )}

              {!frequency && (
                <Switch
                  label="Date Range"
                  value={dateRange}
                  onChange={handleDateRange}
                />
              )}

              {dateRange && (
                <Date
                  label="From"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                />
              )}

              {dateRange && (
                <Date
                  label="To"
                  value={toDate_}
                  onChange={(newValue) => setToDate_(newValue)}
                />
              )}

              <Button
                variant="contained"
                onClick={sendExportRequest}
                fullWidth
              >
                Export
              </Button>
            </Stack>
          </Card>
        )}

        {selectedExport === "exportProjectSchedule" && (
          <Card
            title="Schedule Export Options"
            sx={{ width: "300px" }}
          >
            <Stack
              sx={{ padding: "10px" }}
              spacing={1}
            >
              <Select
                label="Schedule Layout"
                value={scheduleType}
                onChange={(newValue) => setScheduleType(newValue)}
                items={[
                  { id: "frequency", name: "Frequency Columns" },
                  { id: "dateColumns", name: "Date Columns" },
                ]}
              />

              {scheduleType === "dateColumns" && (
                <Date
                  label="From"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                />
              )}

              {scheduleType === "dateColumns" && (
                <Date
                  label="To"
                  value={toDate_}
                  onChange={(newValue) => setToDate_(newValue)}
                />
              )}

              <Button
                variant="contained"
                onClick={sendExportRequest}
                fullWidth
              >
                Export
              </Button>
            </Stack>
          </Card>
        )}

        {/* FileUpload Card */}
        {currentImport !== null && (
          <Card
            title={getTitle(currentImport)}
            sx={{ width: "300px" }}
          >
            <Box padding="10px">
              <FileUpload
                entityName="Project"
                entityId={projectId}
                data={{
                  workType: currentImport,
                  entityName: "Project",
                  projectId: projectId,
                  userId: currentUserId,
                  email: email,
                }}
                onUploaded={handleUploaded}
              />
            </Box>
          </Card>
        )}
      </Box>
    </Page>
  );
};

export default ProjectImportExport;
